/**
 * Thin fetch wrappers around the three public endpoints the widget talks to.
 * Keeps HTTP plumbing out of the UI rendering code so both the Web Component
 * and the React wrapper can reuse it identically.
 */
import type { AuthManager } from './auth.js';
import type { ChatConfig } from './types.js';

export async function fetchConfig(apiBase: string, chatId: string): Promise<ChatConfig> {
  const res = await fetch(`${apiBase}/api/chat-triggers/public/${chatId}`);
  if (!res.ok) throw new Error(`Chat config fetch failed (HTTP ${res.status})`);
  return (await res.json()) as ChatConfig;
}

export async function requestUploadUrl(
  apiBase: string,
  chatId: string,
  body: { fileName: string; mimeType: string; sessionId: string; size: number },
): Promise<{ fileId: string; uploadUrl: string; key: string }> {
  const res = await fetch(`${apiBase}/api/chat-triggers/in/${chatId}/upload-url`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => null);
    throw new Error((err as { error?: string } | null)?.error ?? `upload-url ${res.status}`);
  }
  return res.json();
}

export async function confirmUpload(
  apiBase: string,
  chatId: string,
  fileId: string,
): Promise<{ scanStatus: string }> {
  const res = await fetch(`${apiBase}/api/chat-triggers/in/${chatId}/confirm-upload`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ fileId }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => null);
    throw new Error((err as { error?: string } | null)?.error ?? `confirm ${res.status}`);
  }
  return res.json();
}

export async function pollFileStatus(
  apiBase: string,
  chatId: string,
  fileId: string,
): Promise<{ scanStatus: string; scanMessage?: string }> {
  const res = await fetch(`${apiBase}/api/chat-triggers/in/${chatId}/file-status/${fileId}`);
  if (!res.ok) throw new Error(`file-status ${res.status}`);
  return res.json();
}

/**
 * Discriminated provider ticket the backend returns from
 * /api/voice-sessions/start. Each browser SDK consumes a different
 * bootstrap field — the voice-driver matches on `provider` and forwards
 * the right one (ElevenLabs uses signedUrl, Vapi uses publicKey, Retell
 * uses accessToken). agentId is always present so the driver can fall
 * back to direct mode if needed.
 */
export type VoiceSessionTicket =
  | { provider: 'elevenlabs'; signedUrl: string; ttlSeconds: number; agentId: string }
  | { provider: 'vapi'; publicKey: string; ttlSeconds: number; agentId: string }
  | { provider: 'retell'; accessToken: string; ttlSeconds: number; agentId: string };

/**
 * Mint a one-shot voice session ticket. Required for ElevenLabs voice
 * because forcing the SDK onto its WebSocket transport (away from
 * LiveKit/WebRTC) only works when we hand it a pre-signed URL — public
 * agent_id mode silently ignores `connectionType: 'websocket'` and still
 * negotiates WebRTC, which times out against ElevenLabs's older LiveKit
 * server (debugged 2026-05-02).
 */
export async function fetchVoiceTicket(
  apiBase: string,
  chatId: string,
  sessionId: string,
  auth: AuthManager,
): Promise<VoiceSessionTicket> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const token = await auth.get(sessionId);
  if (token) headers.Authorization = `Bearer ${token.sig}:${token.ts}`;

  const res = await fetch(`${apiBase}/api/voice-sessions/start`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ chatId, sessionId }),
  });

  if (!res.ok) {
    const errBody = await res.text().catch(() => '');
    throw new Error(
      `voice-sessions/start failed (HTTP ${res.status}): ${errBody || res.statusText}`,
    );
  }

  const ticket = (await res.json()) as VoiceSessionTicket;
  if (!ticket?.agentId || !ticket?.provider) {
    throw new Error(
      `voice-sessions/start returned malformed payload — missing agentId or provider`,
    );
  }
  return ticket;
}

/**
 * Open the SSE stream for one message turn. Returns a reader the caller
 * walks with the standard `event:` / `data:` parse loop. Attaches the
 * signed auth header when the manager has one; otherwise sends plain
 * (public-mode triggers ignore the header).
 */
export async function openMessageStream(
  apiBase: string,
  chatId: string,
  auth: AuthManager,
  body: {
    message: string;
    sessionId: string;
    fileRefs?: Array<{ fileId: string }>;
  },
  signal: AbortSignal,
  /** Short-lived preview token from the dashboard iframe, sent as
   *  X-Preview-Token when present. Public ingress accepts it in place
   *  of the HMAC signature for signed triggers. */
  previewToken: string | null = null,
): Promise<Response> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const token = await auth.get(body.sessionId);
  if (token) headers.Authorization = `Bearer ${token.sig}:${token.ts}`;
  if (previewToken) headers['X-Preview-Token'] = previewToken;
  return fetch(`${apiBase}/api/chat-triggers/in/${chatId}/message`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
    signal,
  });
}
